/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Search,
  Bell,
  Moon,
  Sun,
  Menu,
  ChevronDown,
  X,
  LogOut,
  Zap,
  Clock,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AdminContext } from "../context/AdminContext";
import { useTheme } from "../context/ThemeContext";
import LogoutModal from "./LogoutModal";

const Navbar = ({ onMenuClick }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentPages, setRecentPages] = useState([]);
  const searchInputRef = useRef(null);

  const navigate = useNavigate();
  const { aToken, setAToken, adminProfile } = useContext(AdminContext);
  const { isDarkMode, toggleDarkMode } = useTheme();

  // Define quick actions based on role
  const adminQuickActions = [
    {
      title: "Toggle Dark Mode",
      action: () => {
        toggleDarkMode();
        setShowSearch(false);
      },
      category: "Quick Action",
      icon: isDarkMode ? Sun : Moon,
    },
    {
      title: "Logout",
      action: () => {
        setShowSearch(false);
        setShowLogoutModal(true);
      },
      category: "Quick Action",
      icon: LogOut,
    },
  ];

  const quickActions = adminQuickActions;

  // Define search items based on role
  const adminSearchItems = [
    { title: "Dashboard", path: "/dashboard", category: "Navigation" },
    { title: "Caregivers", path: "/caregivers", category: "Navigation" },
    { title: "Clients", path: "/clients", category: "Navigation" },
    { title: "Bookings", path: "/bookings", category: "Navigation" },
    { title: "Jobs", path: "/jobs", category: "Navigation" },
    { title: "Contracts", path: "/contracts", category: "Navigation" },
    { title: "Invoices", path: "/invoices", category: "Navigation" },
    { title: "Reviews", path: "/reviews", category: "Navigation" },
    { title: "Categories", path: "/categories", category: "Navigation" },
    { title: "Blogs", path: "/blogs", category: "Navigation" },
    { title: "Agreements", path: "/agreements", category: "Navigation" },
    { title: "Sub Admins", path: "/admins", category: "Navigation" },
    { title: "Settings", path: "/admins/settings", category: "Settings" },
  ];

  const searchItems = adminSearchItems;

  // Load recent pages from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("adminRecentPages");
    if (stored) {
      setRecentPages(JSON.parse(stored));
    }
  }, []);

  // Save recent page when navigating
  const saveRecentPage = (item) => {
    const storageKey = "adminRecentPages";
    const updated = [
      item,
      ...recentPages.filter((p) => p.path !== item.path),
    ].slice(0, 3); // Keep only 3 recent pages
    setRecentPages(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  // Filter search results - include both pages and quick actions
  const filteredResults = searchQuery.trim()
    ? [
        ...searchItems.filter((item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
        ...quickActions.filter((action) =>
          action.title.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      ]
    : [];

  // Get user name and profile image based on role
  const userName = adminProfile?.name || "Admin";
  const profileImage = adminProfile?.image || "https://via.placeholder.com/150";

  // Get first name or first character for fallback display
  const getDisplayName = () => {
    if (userName) {
      const nameParts = userName.split(" ");
      if (nameParts.length > 0) {
        return nameParts[0]; // Return first name only
      }
      return userName;
    }
    return "A"; // Default: A for Admin
  };

  const getInitials = () => {
    if (userName) {
      const nameParts = userName.split(" ");
      if (nameParts.length > 1) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return userName[0].toUpperCase();
    }
    return "A"; // Default: A for Admin
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    setAToken(null);
    navigate("/");
  };

  // Handle search item click
  const handleSearchItemClick = (item) => {
    if (item.action) {
      // It's a quick action
      item.action();
    } else if (item.path) {
      // It's a page navigation
      saveRecentPage(item);
      navigate(item.path);
      setShowSearch(false);
    }
    setSearchQuery("");
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K or Cmd+K to open search
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      }
      // Escape to close search
      if (e.key === "Escape" && showSearch) {
        setShowSearch(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showSearch]);

  // Focus search input when modal opens
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSearch && !e.target.closest(".search-modal")) {
        setShowSearch(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearch]);

  return (
    <>
      <header
        className={`h-20 flex items-center justify-between px-4 md:px-8 border-b z-10 ${
          isDarkMode
            ? "bg-dark-surface border-dark-border"
            : "bg-card border-accent"
        }`}
      >
        {/* Left */}
        <div className="flex items-center gap-4 flex-1">
          <button
            onClick={onMenuClick}
            className={`md:hidden p-2 rounded-lg ${
              isDarkMode
                ? "text-dark-muted hover:bg-dark-background"
                : "text-text hover:bg-background"
            }`}
          >
            <Menu size={24} />
          </button>

          {/* Mobile Search Button */}
          <button
            onClick={() => setShowSearch(true)}
            className={`md:hidden p-2 rounded-lg ${
              isDarkMode
                ? "text-dark-muted hover:bg-dark-background"
                : "text-text hover:bg-background"
            }`}
          >
            <Search size={20} />
          </button>

          <div
            onClick={() => setShowSearch(true)}
            className={`hidden md:flex items-center px-4 py-2.5 rounded-full border w-full max-w-md cursor-pointer transition-colors ${
              isDarkMode
                ? "bg-dark-background border-dark-border hover:border-primary/50"
                : "bg-white border-accent hover:border-primary/50"
            }`}
          >
            <Search
              size={20}
              className={isDarkMode ? "text-dark-muted" : "text-gray-400"}
            />
            <span
              className={`ml-3 text-sm ${
                isDarkMode ? "text-dark-muted" : "text-gray-400"
              }`}
            >
              Search…
            </span>
            <kbd
              className={`ml-auto px-2 py-0.5 text-xs rounded border ${
                isDarkMode
                  ? "bg-dark-surface border-dark-border text-dark-muted"
                  : "bg-gray-100 border-gray-300 text-gray-500"
              }`}
            >
              Ctrl+K
            </kbd>
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-4">
          {/* Welcome Message */}
          <div
            className={`hidden md:block ${
              isDarkMode ? "text-dark-muted" : "text-gray-600"
            }`}
          >
            Welcome, <span className="font-semibold">{getDisplayName()}</span>
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleDarkMode}
            className={`w-14 p-1 rounded-full relative transition-colors ${
              isDarkMode ? "bg-dark-background" : "bg-accent"
            }`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center transition-transform ${
                isDarkMode
                  ? "translate-x-7 bg-primary"
                  : "translate-x-0 bg-white"
              }`}
            >
              {isDarkMode ? (
                <Moon size={14} className="text-dark-text" />
              ) : (
                <Sun size={14} className="text-secondary" />
              )}
            </div>
          </button>

          {/* Notifications */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className={`p-2 rounded-full relative ${
              isDarkMode
                ? "text-dark-muted hover:bg-dark-background"
                : "text-text hover:bg-background"
            }`}
          >
            <Bell size={22} />
            <span className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full" />
          </button>

          {/* Profile */}
          <div className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className={`flex items-center gap-3 p-1.5 rounded-full ${
                isDarkMode ? "hover:bg-dark-background" : "hover:bg-background"
              }`}
            >
              {profileImage &&
              profileImage !== "https://via.placeholder.com/150" ? (
                <img
                  src={profileImage}
                  alt={userName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    isDarkMode
                      ? "bg-dark-background text-dark-text"
                      : "bg-accent text-secondary"
                  }`}
                >
                  {getInitials()}
                </div>
              )}
              <ChevronDown
                size={16}
                className={isDarkMode ? "text-dark-muted" : "text-gray-400"}
              />
            </button>

            {showProfileMenu && (
              <div
                className={`absolute right-0 mt-2 w-56 rounded-xl shadow-xl border ${
                  isDarkMode
                    ? "bg-dark-surface border-dark-border"
                    : "bg-white border-accent"
                }`}
              >
                <div
                  className={`px-4 py-3 border-b ${
                    isDarkMode ? "border-dark-border" : "border-accent"
                  }`}
                >
                  <p
                    className={`font-medium ${
                      isDarkMode ? "text-dark-text" : "text-text"
                    }`}
                  >
                    {userName}
                  </p>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-dark-muted" : "text-gray-500"
                    }`}
                  >
                    Administrator
                  </p>
                </div>

                <Link
                  to="/admins/settings"
                  className={`block px-4 py-3 text-sm ${
                    isDarkMode
                      ? "text-dark-text hover:bg-dark-background"
                      : "hover:bg-accent"
                  }`}
                  onClick={() => setShowProfileMenu(false)}
                >
                  Settings
                </Link>
                <div
                  className={`px-4 py-3 border-t ${
                    isDarkMode ? "border-dark-border" : "border-accent"
                  }`}
                >
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      setShowLogoutModal(true);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm rounded-lg ${
                      isDarkMode
                        ? "text-red-400 hover:bg-red-400 hover:text-white"
                        : "text-red-500 hover:bg-red-50"
                    } transition-colors`}
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        isDarkMode={isDarkMode}
      />

      {/* Search Modal */}
      {showSearch && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/50 backdrop-blur-sm">
          <div
            className={`search-modal w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 ${
              isDarkMode ? "bg-dark-surface" : "bg-white"
            }`}
          >
            {/* Search Input */}
            <div
              className={`flex items-center gap-3 px-4 py-4 border-b ${
                isDarkMode ? "border-dark-border" : "border-gray-200"
              }`}
            >
              <Search
                size={20}
                className={isDarkMode ? "text-dark-muted" : "text-gray-400"}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search pages and actions..."
                className={`flex-1 bg-transparent outline-none text-base ${
                  isDarkMode
                    ? "text-dark-text placeholder-dark-muted"
                    : "text-text placeholder-gray-400"
                }`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className={`p-1 rounded-lg transition-colors ${
                    isDarkMode
                      ? "hover:bg-dark-background text-dark-muted"
                      : "hover:bg-gray-100 text-gray-400"
                  }`}
                  title="Clear search"
                >
                  <X size={16} />
                </button>
              )}
              <button
                onClick={() => {
                  setShowSearch(false);
                  setSearchQuery("");
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  isDarkMode
                    ? "hover:bg-dark-background text-dark-muted hover:text-dark-text"
                    : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                }`}
                title="Close search (ESC)"
              >
                <X size={18} />
              </button>
            </div>

            {/* Search Results */}
            <div className="max-h-96 overflow-y-auto">
              {searchQuery.trim() === "" ? (
                <div className="py-2">
                  {/* Recent Pages */}
                  {recentPages.length > 0 && (
                    <>
                      <div
                        className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
                          isDarkMode ? "text-dark-muted" : "text-gray-400"
                        }`}
                      >
                        Recent Pages
                      </div>
                      {recentPages.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleSearchItemClick(item)}
                          className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                            isDarkMode
                              ? "hover:bg-dark-background text-dark-text"
                              : "hover:bg-gray-50 text-text"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                isDarkMode
                                  ? "bg-dark-background"
                                  : "bg-gray-100"
                              }`}
                            >
                              <Clock
                                size={16}
                                className={
                                  isDarkMode ? "text-primary" : "text-primary"
                                }
                              />
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-sm">
                                {item.title}
                              </p>
                              <p
                                className={`text-xs ${
                                  isDarkMode
                                    ? "text-dark-muted"
                                    : "text-gray-500"
                                }`}
                              >
                                {item.category}
                              </p>
                            </div>
                          </div>
                          <ChevronDown
                            size={16}
                            className={`-rotate-90 ${
                              isDarkMode ? "text-dark-muted" : "text-gray-400"
                            }`}
                          />
                        </button>
                      ))}
                    </>
                  )}

                  {/* Quick Actions */}
                  <div
                    className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? "text-dark-muted" : "text-gray-400"
                    } ${recentPages.length > 0 ? "mt-2" : ""}`}
                  >
                    Quick Actions
                  </div>
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearchItemClick(action)}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                        isDarkMode
                          ? "hover:bg-dark-background text-dark-text"
                          : "hover:bg-gray-50 text-text"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isDarkMode ? "bg-dark-background" : "bg-gray-100"
                          }`}
                        >
                          {React.createElement(action.icon, {
                            size: 16,
                            className: isDarkMode
                              ? "text-primary"
                              : "text-primary",
                          })}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-sm">{action.title}</p>
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-dark-muted" : "text-gray-500"
                            }`}
                          >
                            {action.category}
                          </p>
                        </div>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`-rotate-90 ${
                          isDarkMode ? "text-dark-muted" : "text-gray-400"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              ) : filteredResults.length > 0 ? (
                <div className="py-2">
                  {filteredResults.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearchItemClick(item)}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                        isDarkMode
                          ? "hover:bg-dark-background text-dark-text"
                          : "hover:bg-gray-50 text-text"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isDarkMode ? "bg-dark-background" : "bg-gray-100"
                          }`}
                        >
                          {item.icon ? (
                            React.createElement(item.icon, {
                              size: 16,
                              className: isDarkMode
                                ? "text-primary"
                                : "text-primary",
                            })
                          ) : (
                            <Search
                              size={16}
                              className={
                                isDarkMode ? "text-primary" : "text-primary"
                              }
                            />
                          )}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-sm">{item.title}</p>
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-dark-muted" : "text-gray-500"
                            }`}
                          >
                            {item.category}
                          </p>
                        </div>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`-rotate-90 ${
                          isDarkMode ? "text-dark-muted" : "text-gray-400"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-8 text-center">
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-dark-muted" : "text-gray-500"
                    }`}
                  >
                    No results found for "{searchQuery}"
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className={`px-4 py-3 border-t flex items-center justify-between text-xs ${
                isDarkMode
                  ? "border-dark-border text-dark-muted"
                  : "border-gray-200 text-gray-500"
              }`}
            >
              <div className="flex items-center gap-4">
                <span>Navigate with ↑↓</span>
                <span>Select with ↵</span>
              </div>
              <span>Press ESC to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
